import type { Contact } from "../types";

// Sprint 9.4 — Export vCard 3.0 client-side (sans backend).
// Spec : https://tools.ietf.org/html/rfc2426

function escape(value: string): string {
  return value
    .replace(/\\/g, "\\\\")
    .replace(/,/g, "\\,")
    .replace(/;/g, "\\;")
    .replace(/\n/g, "\\n");
}

// RFC 2426 §2.6: fold at 75 octets, continuation lines start with a space.
const foldLine = (line: string): string =>
  line.length <= 75
    ? line
    : [line.slice(0, 75), ...(line.slice(75).match(/.{1,74}/g) ?? [])].join(
        "\r\n ",
      );

export type VCardPhoto = { base64: string; mimeType: string };

export function generateVCard(
  contact: Contact,
  companyName?: string,
  photo?: VCardPhoto,
): string {
  const fullName = [contact.first_name, contact.last_name]
    .filter(Boolean)
    .join(" ");
  const lines: string[] = [
    "BEGIN:VCARD",
    "VERSION:3.0",
    `FN:${escape(fullName)}`,
    `N:${escape(contact.last_name ?? "")};${escape(contact.first_name ?? "")};;;`,
  ];
  if (contact.title) lines.push(`TITLE:${escape(contact.title)}`);
  const org = companyName ?? contact.company_name;
  if (org) lines.push(`ORG:${escape(org)}`);
  for (const e of contact.email_jsonb ?? []) {
    const type =
      e.type === "Work" ? "WORK" : e.type === "Home" ? "HOME" : "OTHER";
    lines.push(`EMAIL;TYPE=${type}:${escape(e.email)}`);
  }
  for (const p of contact.phone_jsonb ?? []) {
    const type =
      p.type === "Work" ? "WORK" : p.type === "Home" ? "HOME" : "CELL";
    lines.push(`TEL;TYPE=${type}:${escape(p.number)}`);
  }
  if (contact.linkedin_url) {
    lines.push(`URL:${escape(contact.linkedin_url)}`);
  }
  if (contact.background) {
    lines.push(`NOTE:${escape(contact.background)}`);
  }
  if (photo) {
    const type = photo.mimeType.split("/")[1]?.toUpperCase() || "PNG";
    lines.push(foldLine(`PHOTO;ENCODING=b;TYPE=${type}:${photo.base64}`));
  }
  lines.push("END:VCARD");
  return lines.join("\r\n");
}

export function downloadVCard(
  contact: Contact,
  companyName?: string,
  photo?: VCardPhoto,
): void {
  const vcard = generateVCard(contact, companyName, photo);
  const blob = new Blob([vcard], { type: "text/vcard;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  const slug =
    [contact.first_name, contact.last_name]
      .filter(Boolean)
      .join("-")
      .toLowerCase()
      .replace(/[^a-z0-9-]/g, "") || "contact";
  a.download = `${slug}.vcf`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
